/**
 * Vessel kinematics: spawning, steering, buoyancy and the friendly approach loop.
 *
 * Vessels steer in 2D on the XZ plane and take their vertical motion entirely from the
 * analytic wave field — the same function the GPU displaces the surface with, which is
 * what makes a hull sit *in* the water rather than on a guess about it.
 */

import type { Vessel, VesselKind, EntityId, Cargo } from './types';
import type { WaveField } from './waves';
import { sampleBuoyancy } from './waves';
import type { Rng } from './rng';
import { angleDelta, clamp, dirFromHeading, turnToward } from './math';
import { vesselSpec } from '../content/vessels';
import { SAFETY_ZONE_RADIUS, SAFE_LINE_METRES, RIG_KEEP_OUT_METRES } from './constants';

/**
 * Whether the rig's keep-out ring applies to this vessel.
 *
 * Hostiles only — a friendly's whole job is to come alongside and berth. The creature is
 * exempt, limbs included: reaching the platform is the finale, not a bug. See
 * `RIG_KEEP_OUT_METRES`.
 */
export function mustKeepClearOfRig(v: Vessel): boolean {
  return v.faction === 'hostile' && v.kind !== 'kraken' && v.kind !== 'tentacle';
}

/**
 * Push a vessel back out to the keep-out ring if it has crossed inside.
 *
 * Radial and position-level on purpose. Steering preferences can be overridden by any
 * future behaviour, a separation nudge or a spawn placement; this cannot, which is what
 * makes "no enemy can get under the rig" a guarantee rather than an intention. Bearing is
 * preserved, so a hull that touches the ring slides along it instead of being teleported.
 *
 * Returns true when it actually moved something, so tests can assert the wall is load
 * bearing rather than merely present.
 */
export function enforceRigKeepOut(v: Vessel): boolean {
  if (!mustKeepClearOfRig(v)) return false;
  const range = Math.hypot(v.pos.x, v.pos.z);
  if (range >= RIG_KEEP_OUT_METRES) return false;
  if (range < 1e-3) {
    // Dead centre has no bearing to preserve; north is arbitrary but deterministic.
    v.pos.z = RIG_KEEP_OUT_METRES;
    return true;
  }
  const scale = RIG_KEEP_OUT_METRES / range;
  v.pos.x *= scale;
  v.pos.z *= scale;
  return true;
}

/**
 * Strategic approach compression (plan E1). Beyond 6 km a vessel's route progress runs at
 * ×4 so a 10 km inbound resolves inside one shift; between 6 and 5 km it lerps back to
 * ×1. At those ranges the angular motion on screen is minuscule, so this is invisible —
 * it exists purely so the arithmetic of a 12-minute shift works against real sea distances.
 *
 * It is strictly a per-entity route-progress multiplier: it advances position along a
 * track and nothing else. Waves, weapons, timers and oil production always run real-time.
 */
export const COMPRESSION_FAR_METRES = 6000;
export const COMPRESSION_NEAR_METRES = 5000;
export const COMPRESSION_MAX = 4;

/**
 * @param nearMetres Range at which route progress returns to real time. Defaults to the
 *   plan's 5 km; a lull passes `LULL_ROUTE_NEAR_METRES` so a friendly's approach keeps
 *   compressing while the player has nothing to shoot at. The ramp always ends at
 *   `COMPRESSION_FAR_METRES`, so lowering the near edge widens the ramp rather than
 *   shifting it — no step change when a lull begins or ends mid-approach.
 */
export function routeTimeScale(
  rangeMetres: number,
  engaged: boolean,
  nearMetres: number = COMPRESSION_NEAR_METRES,
  maxScale: number = COMPRESSION_MAX,
): number {
  if (engaged) return 1;
  if (rangeMetres <= nearMetres) return 1;
  if (rangeMetres >= COMPRESSION_FAR_METRES) return maxScale;
  const t = (rangeMetres - nearMetres) / (COMPRESSION_FAR_METRES - nearMetres);
  return 1 + (maxScale - 1) * t;
}

let nextEntityId = 1;

export function resetEntityIds(): void {
  nextEntityId = 1;
}

export function createVessel(
  kind: VesselKind,
  x: number,
  z: number,
  heading: number,
  cargo?: Cargo,
): Vessel {
  const spec = vesselSpec(kind);
  const vessel: Vessel = {
    id: nextEntityId++,
    kind,
    faction: spec.faction,
    state: 'inbound',
    pos: { x, y: 0, z },
    speed: spec.speed,
    heading,
    hp: spec.hp,
    maxHp: spec.hp,
    pitch: 0,
    roll: 0,
    detection: 'undetected',
    detectedFor: 0,
    targetId: null,
    weaponCooldown: 0,
    stateTimer: 0,
    transferTimer: 0,
    goalX: 0,
    goalZ: 0,
    timeScale: 1,
    resolved: false,
    safe: false,
    /*
     * Negative infinity means "has always been here".
     *
     * Only the caller that owns the clock can stamp a real spawn time, so the default is
     * the one that makes every hull created outside a running shift — tests, benchmark
     * staging, the fly-through — arrive fully formed rather than caught mid-emergence.
     */
    spawnedAt: Number.NEGATIVE_INFINITY,
    /*
     * Mini-subs dive as soon as they are under way; everything else runs on the surface.
     *
     * The Kraken counts as a diver because its first phase is always `dread`, which holds
     * it under. Without this it spends exactly one frame surfaced and detectable: the
     * vessel is created during `stepVessels`, which runs detection, and `stepKrakenEncounter`
     * — which submerges it — runs after. One sixtieth of a second, but it is a radar
     * return and a silhouette for the thing the whole phase exists to withhold.
     */
    submerged: kind === 'minisub' || kind === 'kraken',
    // Starts matching `submerged` so a hull that spawns dived is already down rather
    // than sinking into frame on its first second.
    submergence: kind === 'minisub' || kind === 'kraken' ? 1 : 0,
    // Overwritten every tick by whatever drives the hull; this is only the value it
    // spawns holding, before its first AI step.
    submergedDepth: 0,
    trackAge: 0,
    lastKnownX: x,
    lastKnownZ: z,
  };
  if (cargo) vessel.cargo = cargo;
  return vessel;
}

/** Spawn on a bearing at a range, already pointed at the rig. */
export function spawnAtBearing(
  kind: VesselKind,
  bearingDeg: number,
  rangeMetres: number,
  cargo?: Cargo,
): Vessel {
  const bearing = (bearingDeg * Math.PI) / 180;
  const dir = dirFromHeading(bearing);
  const x = dir.x * rangeMetres;
  const z = dir.z * rangeMetres;
  // Heading points back toward the origin.
  const heading = Math.atan2(-x, -z);
  return createVessel(kind, x, z, heading, cargo);
}

/**
 * Advance one vessel's position, heading and attitude.
 *
 * Hostile behaviour decides `goalX/goalZ` and `speed` before this runs; this function is
 * purely "get there and ride the water", so steering and buoyancy stay in one place for
 * every vessel type.
 */
export function stepVesselMotion(
  v: Vessel,
  waves: WaveField,
  time: number,
  dt: number,
  rng: Rng,
  engaged: boolean,
  /** Range at which route progress returns to real time; see `routeTimeScale`. */
  nearMetres: number = COMPRESSION_NEAR_METRES,
  /** Peak route compression beyond `COMPRESSION_FAR_METRES`. */
  maxScale: number = COMPRESSION_MAX,
): void {
  const spec = vesselSpec(v.kind);

  if (v.state === 'sinking') {
    v.stateTimer += dt;

    // Two phases, per creative direction: she burns and smokes on the surface first,
    // then goes down. Bigger hulls linger in both — a tanker is a burning silhouette for
    // half a minute before the water starts taking her, and minutes more after that.
    const smoking = v.stateTimer < spec.smokeDuration;

    // Way comes off fast once the engines are gone; the bigger the hull, the longer she
    // carries her momentum.
    const dragRate = 2.5 * (30 / Math.max(spec.length, 12));
    v.speed = Math.max(0, v.speed - dt * dragRate);
    v.pos.x += Math.sin(v.heading) * v.speed * dt;
    v.pos.z += Math.cos(v.heading) * v.speed * dt;
    // A burning wreck carries its way, and could coast in under the deck. The ring is
    // unconditional so the invariant holds for every hostile in every state.
    enforceRigKeepOut(v);

    if (smoking) {
      // Still floating: she rides the swell like any other hull, but takes on a growing
      // list as compartments flood. The list is the tell that she is not coming back.
      const t = v.stateTimer / spec.smokeDuration;
      const buoy = sampleBuoyancy(
        waves, v.pos.x, v.pos.z, v.heading,
        spec.length * 0.5, spec.beam * 0.5, time,
      );
      v.pos.y = buoy.height;
      v.pitch = buoy.pitch;
      v.roll = buoy.roll + Math.sign(v.roll || 1) * 0.22 * t * t;
      return;
    }

    // Settling. Sink depth follows a slow ease-in: barely moving at first, then the
    // stern (or bow) goes and she slides under.
    const t = Math.min(1, (v.stateTimer - spec.smokeDuration) / spec.sinkDuration);
    const submergence = Math.pow(t, 2.2);
    const buoy = sampleBuoyancy(
      waves, v.pos.x, v.pos.z, v.heading,
      spec.length * 0.5, spec.beam * 0.5, time,
    );
    // Interpolate off the wave surface toward well below it, so she leaves the water
    // rather than being teleported down through it.
    const depth = spec.height + spec.draft + 6;
    v.pos.y = buoy.height - submergence * depth;
    v.pitch = buoy.pitch - submergence * 0.42;
    v.roll = buoy.roll + Math.sign(v.roll || 1) * (0.22 + submergence * 0.95);

    if (t >= 1) v.state = 'dead';
    return;
  }

  const range = Math.hypot(v.pos.x, v.pos.z);
  /*
   * `engaged` is the caller's decision and is trusted as given.
   *
   * This used to re-derive it here as `engaged || attacking || hp < maxHp`, which meant
   * the parameter could only ever add to the condition, never relax it — and `hp < maxHp`
   * is permanent, so any hull that had ever been scratched was locked at real time for
   * the remainder of the shift no matter what the caller knew about the sector being
   * empty. Only `world.ts` can tell "under threat now" from "hurt once", so it decides.
   */
  v.timeScale = routeTimeScale(range, engaged, nearMetres, maxScale);
  const routeDt = dt * v.timeScale;

  // Steering: turn toward the goal at the hull's turn rate.
  const dx = v.goalX - v.pos.x;
  const dz = v.goalZ - v.pos.z;
  if (Math.hypot(dx, dz) > 1) {
    const want = Math.atan2(dx, dz);
    v.heading = turnToward(v.heading, want, spec.turnRate * routeDt);
  }

  // Wave-induced yaw noise, so a hull is never on rails. Seeded, therefore replayable.
  if (v.kind !== 'minisub') {
    const noise = (rng.next() - 0.5) * 0.0025 * (1 + spec.speed / 12);
    v.heading += noise;
  }

  v.pos.x += Math.sin(v.heading) * v.speed * routeDt;
  v.pos.z += Math.cos(v.heading) * v.speed * routeDt;
  // The wall, applied before buoyancy so attitude is sampled where the hull ends up.
  enforceRigKeepOut(v);

  // Buoyancy: attitude comes from a plane fit across the hull's own footprint, so a
  // 180 m tanker ignores chop a 9 m skiff is thrown around by.
  const buoy = sampleBuoyancy(
    waves, v.pos.x, v.pos.z, v.heading,
    spec.length * 0.5, spec.beam * 0.5, time,
  );
  v.pos.y = buoy.height;
  v.pitch = buoy.pitch;
  v.roll = buoy.roll;

  // Small craft carry an extra chop term: the detail layer they actually ride is not in
  // the physics field, and without this they look welded to the swell.
  if (spec.length < 40) {
    const bob = Math.sin(time * 2.6 + v.id * 1.7) * 0.22 + Math.sin(time * 4.1 + v.id) * 0.12;
    v.pos.y += bob;
    v.roll += Math.sin(time * 1.9 + v.id * 2.3) * 0.05;
  }
}

/**
 * Friendly approach logic: steam to the rig, slow on the run-in, transfer inside the
 * ring, then leave. Returns 'arrived' on the tick the safety ring is crossed and
 * 'delivered' on the tick the transfer completes, so the caller can fire events.
 */
export function stepFriendlyApproach(
  v: Vessel,
  dt: number,
  transferSeconds: number,
): 'none' | 'safe' | 'arrived' | 'delivered' | 'gone' {
  const spec = vesselSpec(v.kind);
  const range = Math.hypot(v.pos.x, v.pos.z);

  if (v.state === 'inbound') {
    // Aim just short of the rig; nothing may steam into the structure.
    const bearing = Math.atan2(v.pos.x, v.pos.z);
    v.goalX = Math.sin(bearing) * (SAFETY_ZONE_RADIUS * 0.72);
    v.goalZ = Math.cos(bearing) * (SAFETY_ZONE_RADIUS * 0.72);
    /*
     * The run-in slowdown belongs to the berth, not to the Safe Line.
     *
     * It used to begin at 1 km, which laid it directly across the last stretch a shift
     * waits on: 200 m at 0.8x cost over twenty seconds of empty sea for no reason the
     * player could see. She holds her own speed until she is safe, and eases off only
     * for the approach to the berth after that.
     */
    v.speed = range < SAFE_LINE_METRES ? spec.speed * 0.8 : spec.speed;

    /*
     * The Safe Line. Everything about the arrival resolves here — see `SAFE_LINE_METRES`
     * — and nothing about her behaviour changes: she keeps steaming in to dock exactly
     * as before, and the berth below is now pure theatre.
     */
    if (!v.safe && range <= SAFE_LINE_METRES) {
      v.safe = true;
      return 'safe';
    }

    if (range <= SAFETY_ZONE_RADIUS) {
      v.state = 'transferring';
      v.transferTimer = transferSeconds;
      return 'arrived';
    }
    return 'none';
  }

  if (v.state === 'transferring') {
    v.speed = Math.max(0, v.speed - dt * 1.5);
    v.transferTimer -= dt;
    if (v.transferTimer <= 0) {
      v.state = 'departing';
      // Head back out the way she came.
      const bearing = Math.atan2(v.pos.x, v.pos.z);
      v.goalX = Math.sin(bearing) * 14000;
      v.goalZ = Math.cos(bearing) * 14000;
      return 'delivered';
    }
    return 'none';
  }

  if (v.state === 'departing') {
    v.speed = Math.min(spec.speed, v.speed + dt * 1.2);
    if (range > 12000) {
      v.state = 'dead';
      return 'gone';
    }
    return 'none';
  }

  return 'none';
}

/**
 * Separation steering: no two hulls within 150 m (plan E1). Applied as a goal nudge
 * rather than a position correction, so vessels never teleport apart or jitter.
 */
export function applySeparation(vessels: readonly Vessel[]): void {
  const MIN_SEP = 150;
  for (let i = 0; i < vessels.length; i++) {
    const a = vessels[i] as Vessel;
    if (a.state === 'dead' || a.state === 'sinking') continue;
    for (let j = i + 1; j < vessels.length; j++) {
      const b = vessels[j] as Vessel;
      if (b.state === 'dead' || b.state === 'sinking') continue;
      const dx = b.pos.x - a.pos.x;
      const dz = b.pos.z - a.pos.z;
      const d = Math.hypot(dx, dz);
      if (d > MIN_SEP || d < 1e-3) continue;
      // Push each goal laterally away from the other, proportional to the overlap.
      const push = ((MIN_SEP - d) / MIN_SEP) * 90;
      const nx = dx / d;
      const nz = dz / d;
      a.goalX -= nx * push;
      a.goalZ -= nz * push;
      b.goalX += nx * push;
      b.goalZ += nz * push;
    }
  }
}

/** Distance from the rig at the origin. */
export function rangeToRig(v: Vessel): number {
  return Math.hypot(v.pos.x, v.pos.z);
}

/** Heading a vessel would need to point at a world point. */
export function bearingTo(v: Vessel, x: number, z: number): number {
  return Math.atan2(x - v.pos.x, z - v.pos.z);
}

/** True when the vessel is pointed within `tolerance` radians of a world point. */
export function isFacing(v: Vessel, x: number, z: number, tolerance: number): boolean {
  return Math.abs(angleDelta(v.heading, bearingTo(v, x, z))) < tolerance;
}

/** Clamp helper used by AI when picking stand-off distances. */
export function standOff(range: number, min: number, max: number): number {
  return clamp(range, min, max);
}

export function findVessel(vessels: readonly Vessel[], id: EntityId | null): Vessel | undefined {
  if (id === null) return undefined;
  return vessels.find((v) => v.id === id);
}
